import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const wholesaleDealsApi = createApi({
  reducerPath: "wholesaleDealsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WholesaleDeal", "WholesaleDealStats", "WholesalePipeline"],
  endpoints: (build) => ({
    // Get all deals
    getAllDeals: build.query({
      query: ({ stage, search, page = 1, limit = 100 } = {}) => ({
        url: "/wholesale/deals",
        method: "GET",
        params: { stage, search, page, limit },
      }),
      providesTags: ["WholesaleDeal"],
    }),

    // Get deals by pipeline
    getDealsByPipeline: build.query({
      query: () => ({
        url: "/wholesale/deals/pipeline",
        method: "GET",
      }),
      providesTags: ["WholesalePipeline", "WholesaleDeal"],
    }),

    // Get deal by ID
    getDealById: build.query({
      query: (id) => ({
        url: `/wholesale/deals/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "WholesaleDeal", id }],
    }),

    // Create deal
    createDeal: build.mutation({
      query: (deal) => ({
        url: "/wholesale/deals",
        method: "POST",
        body: deal,
      }),
      invalidatesTags: ["WholesaleDeal", "WholesaleDealStats", "WholesalePipeline"],
    }),

    // Update deal
    updateDeal: build.mutation({
      query: ({ id, ...deal }) => ({
        url: `/wholesale/deals/${id}`,
        method: "PUT",
        body: deal,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleDeal", id },
        "WholesaleDeal",
        "WholesaleDealStats",
        "WholesalePipeline",
      ],
    }),

    // Delete deal
    deleteDeal: build.mutation({
      query: (id) => ({
        url: `/wholesale/deals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WholesaleDeal", "WholesaleDealStats", "WholesalePipeline"],
    }),

    // Update deal stage
    updateDealStage: build.mutation({
      query: ({ id, stage }) => ({
        url: `/wholesale/deals/${id}/stage`,
        method: "PATCH",
        body: { stage },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleDeal", id },
        "WholesaleDeal",
        "WholesaleDealStats",
        "WholesalePipeline",
      ],
    }),

    // Assign buyer to deal
    assignBuyerToDeal: build.mutation({
      query: ({ id, buyerId }) => ({
        url: `/wholesale/deals/${id}/assign-buyer`,
        method: "POST",
        body: { buyerId },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleDeal", id },
        "WholesaleDeal",
        "WholesalePipeline",
      ],
    }),

    // Add timeline event
    addTimelineEvent: build.mutation({
      query: ({ id, event, notes }) => ({
        url: `/wholesale/deals/${id}/timeline`,
        method: "POST",
        body: { event, notes },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "WholesaleDeal", id }],
    }),

    // Get deal stats
    getDealStats: build.query({
      query: () => ({
        url: "/wholesale/deals/stats",
        method: "GET",
      }),
      providesTags: ["WholesaleDealStats"],
    }),

    // Get deals needing attention
    getDealsNeedingAttention: build.query({
      query: () => ({
        url: "/wholesale/deals/needs-attention",
        method: "GET",
      }),
      providesTags: ["WholesaleDeal"],
    }),

    // Generate a signed upload-token URL for a buyer to upload docs.
    // The /buyer-docs endpoint requires this token; without it the buyer's
    // submit will 401. A lazy query so the link is only minted when the
    // user clicks "Copy buyer upload link" (token has a 14-day TTL).
    getBuyerUploadToken: build.query({
      query: (id) => ({
        url: `/wholesale/deals/${id}/upload-token`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAllDealsQuery,
  useGetDealsByPipelineQuery,
  useGetDealByIdQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useDeleteDealMutation,
  useUpdateDealStageMutation,
  useAssignBuyerToDealMutation,
  useAddTimelineEventMutation,
  useGetDealStatsQuery,
  useGetDealsNeedingAttentionQuery,
  useLazyGetBuyerUploadTokenQuery,
} = wholesaleDealsApi;
